import React, { ReactElement } from 'react';
import { FieldGroupItem, FieldGroupVariant } from '@dexma/ui-components';

import FormFieldGroup from 'adapter/in/common/components/FieldGroup';
import { FieldGroupContainerProps, onChangeFieldGroup } from 'adapter/in/common/form/FieldGroup';
import { SelectorFieldDataImpl, SelectorOption, SelectorType } from 'adapter/in/common/form/types';
import { useBaseDefaultValuesSelector } from 'adapter/in/common/hooks/form/useBaseDefaultValuesSelector';
import { isSelectorFieldDataValid, useBaseSelectorContainer } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import i18n from 'asset/translation';
import PresetFormFieldProperties from 'domain/entity/preset/form/presetFormFieldProperties';

type DefaultValuesFieldGroupContainerProps = FieldGroupContainerProps & {
    parseOptions?: (opts: SelectorOption[] | undefined) => SelectorOption[];
    properties: PresetFormFieldProperties;
    style?: React.CSSProperties;
};

export const DefaultValuesFieldGroupContainer = ({
    name,
    configType,
    dataDependsOn,
    label,
    affectsTo,
    properties,
    parseOptions,
    style,
    footer,
}: DefaultValuesFieldGroupContainerProps): ReactElement => {
    const rules =
        name === 'resolution-0'
            ? {}
            : {
                  required: i18n.t('widgets.errorNotEmpty').toString(),
                  validate: (v: SelectorFieldDataImpl) => isSelectorFieldDataValid(1, 1)(v),
              };
    const { value, onChange, invalid, error, shouldLoad } = useBaseSelectorContainer(name, rules, undefined, dataDependsOn, affectsTo);
    const defaultValues = properties?.defaultValues ? properties?.defaultValues : [];
    const { options, isLoading } = useBaseDefaultValuesSelector<SelectorOption>(
        SelectorType.FIELD_GROUP,
        configType,
        value,
        shouldLoad,
        defaultValues,
        onChange
    );
    const optionsAvailable = parseOptions ? parseOptions(options) : options;

    return (
        <FormFieldGroup
            style={style}
            name={name}
            isLoading={isLoading}
            label={label}
            options={(optionsAvailable as FieldGroupItem[]) || []}
            onChange={onChangeFieldGroup(onChange, configType)}
            value={value?.value}
            error={invalid && !!error && error.type !== 'loading'}
            variant={name === 'resolution-0' ? FieldGroupVariant.JOINED : FieldGroupVariant.SPLIT}
            footer={footer}
        />
    );
};

export default DefaultValuesFieldGroupContainer;
